/*
  Sisters Moto: tire finder (sections/sm-tire-finder.liquid).
  - Loads a compact product index from /collections/<handle>?view=tire-index&page=N
    (templates/collection.tire-index.liquid) for every source collection (section setting
    "Tire collections"; duplicates removed), 4 requests at a time, cached in sessionStorage for 30 minutes.
  - Reads tire sizes, position, category and compound from product titles + "Tire ..." tags.
  - Search by size (free text or Width > Aspect > Rim), position, category, compound,
    brand, in stock; shareable URL (?size=110/90-19&pos=rear&cat=mx&brand=Dunlop&stock=1).
  - Add to cart posts to /cart/add.js; snippets/sm-cart-drawer.liquid opens the cart drawer.
  The parser is exported for Node (module.exports) so it can be tested against product data.
*/
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.SmTireFinder = api;
    if (typeof document !== 'undefined') {
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', api.boot);
      else api.boot();
    }
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Parser                                                              */
  /* ------------------------------------------------------------------ */

  // Words that mark accessories (tools, tubes, sealant...) rather than tires.
  var ACCESSORY = /\b(axles?|wrench\w*|tools?|levers?|irons?|gauges?|sealants?|valves?|tubes?|beads?|rim ?locks?|spoons?|stands?|patch\w*|plugs?|pluggers?|repair|kits?|mousse|liners?|balanc\w*|wraps?|racks?|warmers?|chains?|pressure|changer|changing|inflat\w*|spacers?|covers?|bags?|chocks?|bungies|tie-?downs?|adapters?|sliders?|bearings?|sign|pens?|wedges?|stoppers?|holders?|brush|without)\b/i;
  // Accessory words that never appear in a real tire title, even one with a size in it.
  var ACCESSORY_STRONG = /\b(axles?|wrench\w*|levers?|irons?|gauges?|sealants?|valves?|tubes?|rim ?locks?|spoons?|plugs?|pluggers?|repair|kits?|mousse|liners?|warmers?|chains?|changer|racks?)\b/i;

  // 110/90-19, 180/55R17, 150/70B18, 140/60ZR17, 200/55VR17, 110/90 19, 80/100 - 21
  var RX_METRIC = /(^|[^\d.\/])(\d{2,3})\s*\/\s*(\d{2,3})\s*(?:-\s*)?(ZR|VR|HR|WR|R|B)?\s*(?:-\s*)?(\d{1,2})(?![\d.\/])/i;
  // 80100 21 (titles without the slash, e.g. Stark Varg)
  var RX_NOSLASH = /(^|\s)(\d{2,3})(100|90|80|70|65|60|55|50)\s+(\d{2})(?=\s|$)/;
  // 3.00-16, 4.60"-18", 4.10 18, 2.50"-10", 3.50-8
  var RX_INCH = /(^|[^\d.\/])(\d{1,2}\.\d{2})\s*"?\s*(?:-|\s|R)\s*(\d{1,2})\s*"?(?![\d.\/])/;
  // Alpha sizes: MT90-16, MT90 16, MU85B16, MH90-21, MJ90-19 (shown as-is, matched as their metric equivalent)
  var RX_ALPHA = /(^|[^\w])M([HJMNPRTUV])(85|90)\s*(?:-|B|\s)\s*(\d{2})(?![\d.\/])/i;
  var ALPHA_EQ = { H: '80/90', J: '90/90', M: '100/90', N: '110/90', P: '120/80', R: '120/90', T: '130/90', U: '140/90', V: '150/80' };
  // ATV / flotation sizes: 25x8R12, 25x8r 12, 26x9-12, 30x10R14, 26x7 17
  var RX_FLOT = /(^|[^\d.\/])(\d{2})\s*x\s*(\d{1,2}(?:\.\d{1,2})?)\s*(?:-|R|\s)\s*(\d{1,2})(?![\d.\/])/i;

  function validMetric(w, a, r) {
    return w >= 50 && w <= 360 && a >= 25 && a <= 130 && r >= 6 && r <= 26;
  }
  function validInch(w, r) {
    return w >= 2 && w <= 7.5 && r >= 6 && r <= 24;
  }

  // Find the first tire size in a string. Returns { w, a, r, key, kind, cons, index, len } or null.
  function findSize(str) {
    if (!str) return null;
    var s = String(str);
    var best = null;
    function consider(c) { if (c && (!best || c.index < best.index)) best = c; }
    var m = RX_METRIC.exec(s);
    if (m) {
      var w = +m[2], a = +m[3], r = +m[5];
      if (validMetric(w, a, r)) {
        consider({ kind: 'metric', w: String(w), a: String(a), r: String(r), cons: (m[4] || '').toUpperCase(),
          index: m.index + m[1].length, len: m[0].length - m[1].length });
      }
    }
    m = RX_NOSLASH.exec(s);
    if (m) {
      var w2 = +m[2], a2 = +m[3], r2 = +m[4];
      if (validMetric(w2, a2, r2)) {
        consider({ kind: 'metric', w: String(w2), a: String(a2), r: String(r2), cons: '',
          index: m.index + m[1].length, len: m[0].length - m[1].length });
      }
    }
    m = RX_INCH.exec(s);
    if (m) {
      var wi = parseFloat(m[2]), ri = +m[3];
      if (validInch(wi, ri)) {
        consider({ kind: 'inch', w: wi.toFixed(2), a: '', r: String(ri), cons: '',
          index: m.index + m[1].length, len: m[0].length - m[1].length });
      }
    }
    m = RX_ALPHA.exec(s);
    if (m) {
      var eq = ALPHA_EQ[m[2].toUpperCase()].split('/');
      consider({ kind: 'metric', w: eq[0], a: eq[1], r: String(+m[4]), cons: /B/i.test(m[0]) ? 'B' : '',
        alias: ('M' + m[2] + m[3] + '-' + (+m[4])).toUpperCase(), index: m.index + m[1].length, len: m[0].length - m[1].length });
    }
    m = RX_FLOT.exec(s);
    if (m && +m[2] >= 18 && +m[2] <= 40 && +m[4] >= 8 && +m[4] <= 17) {
      consider({ kind: 'flot', w: m[2] + 'x' + m[3], a: '', r: String(+m[4]), cons: /R/i.test(m[0].slice(m[1].length)) ? 'R' : '',
        index: m.index + m[1].length, len: m[0].length - m[1].length });
    }
    if (best) best.key = sizeKey(best.w, best.a, best.r);
    return best;
  }

  // "300 21" style inch widths typed without the dot
  var INCH_3DIGIT = ['225', '250', '275', '300', '325', '350', '400', '410', '450', '460', '510'];

  function sizeKey(w, a, r) {
    if (!w) return '';
    if (!a) return r ? w + '-' + r : w;
    return w + '/' + a + (r ? '-' + r : '');
  }

  // Normalise what a shopper types into { w, a, r } (any part may be '').
  // Accepts 110/90-19, 110 90 19, 110/90r19, 110-90-19, 110/90, 3.00-21, 3.00 21, 300-21, 3-21.
  function parseQuery(q) {
    var out = { w: '', a: '', r: '' };
    if (!q) return out;
    var s = String(q).trim().toUpperCase().replace(/ZR|VR|HR|WR/g, '-').replace(/(\d)\s*[RB]\s*(\d)/g, '$1-$2');
    var full = findSize(s);
    if (full) return { w: full.w, a: full.a, r: full.r };
    var t = s.split(/[^\d.]+/).filter(Boolean);
    if (!t.length) return out;
    function inchW(x) {
      var n = parseFloat(x);
      if (/^\d{3}$/.test(x) && n >= 200 && n <= 750) n = n / 100; // "300" -> 3.00
      return n >= 2 && n <= 7.5 ? n.toFixed(2) : '';
    }
    var first = t[0];
    var isInch = first.indexOf('.') > -1 || parseFloat(first) < 10;
    if (!isInch && t.length === 2 && INCH_3DIGIT.indexOf(first) > -1 && +t[1] <= 24) isInch = true;
    if (isInch) {
      out.w = inchW(first);
      if (t[1] && +t[1] >= 6 && +t[1] <= 24) out.r = String(+t[1]);
      return out;
    }
    if (/^\d{4,6}$/.test(first) && t.length <= 2) {
      // 11090 or 11090 19 (no separators)
      var m = /^(\d{2,3})(\d{2,3})$/.exec(first);
      var cands = [];
      for (var cut = 2; cut <= 3; cut++) {
        var ww = +first.slice(0, cut), aa = +first.slice(cut);
        if (ww >= 50 && ww <= 360 && aa >= 25 && aa <= 130 && first.length - cut >= 2) cands.push([ww, aa]);
      }
      if (m && cands.length) {
        out.w = String(cands[cands.length - 1][0]);
        out.a = String(cands[cands.length - 1][1]);
        if (t[1]) out.r = String(+t[1]);
        return out;
      }
    }
    var w = +first;
    if (w >= 50 && w <= 360) out.w = String(w);
    if (t[1]) {
      var n = +t[1];
      if (t.length === 2 && n <= 24 && n >= 6 && !(n >= 25)) out.r = String(n);
      else if (n >= 25 && n <= 130) out.a = String(n);
    }
    if (t[2] && +t[2] >= 6 && +t[2] <= 24) out.r = String(+t[2]);
    return out;
  }

  var CATEGORIES = [
    { id: 'mx', label: 'MX / Off-road' },
    { id: 'dual', label: 'Dual sport & Adventure' },
    { id: 'enduro', label: 'Enduro' },
    { id: 'street', label: 'Street & Sport' },
    { id: 'cruiser', label: 'Cruiser' },
    { id: 'scooter', label: 'Scooter & Moped' },
    { id: 'trials', label: 'Trials' },
    { id: 'sand', label: 'Sand' },
    { id: 'atv', label: 'ATV / UTV' }
  ];
  var TERRAINS = [
    { id: 'soft', label: 'Soft' },
    { id: 'intermediate', label: 'Intermediate' },
    { id: 'hard', label: 'Hard' },
    { id: 'sandmud', label: 'Sand / Mud' }
  ];

  function categorize(title, tags, size) {
    var c = {};
    var tg = (tags || []).join('|');
    var t = ' ' + title + ' ';
    if (/Tire M X|Motorcycle Tires - Off Road/i.test(tg)) c.mx = 1;
    if (/Tire Dual S PO Rt/i.test(tg)) c.dual = 1;
    if (/Tire Scooter|Tire Moped/i.test(tg)) c.scooter = 1;
    if (/Tire Trials/i.test(tg)) c.trials = 1;
    if (/Tire Offroad Sand/i.test(tg)) c.sand = 1;
    if (/Tire Street/i.test(tg)) c.street = 1;

    if (/\b(mx\w*|motocross|off-?road|cross|starcross|geomax|battlecross|maxxcross|washougal|millville|terra force-mx|mx\s?\d+|ix\d+|vx\d+|sx\d*|m5b|ve-?33|mud|flat track)\b/i.test(t) && !/dual sport|adventure/i.test(t)) c.mx = 1;
    if (/\b(dual ?sport|dual purpose|adventure|adventurecross|adv|trail|trailmax|rally|tourance|karoo|anakee|tkc|scorpion\*? (rally|trail|str)|e-?07|e-?09|t63|tw\d+|ax41|k270|k761|k784|big block|dakar|stone king|mt-?21|d6\d\d|d60\d|244|705|804|805)\b/i.test(t)) c.dual = 1;
    if (/\b(enduro|xc|hard enduro|gummy|extreme enduro|six days|ex-?\d*)\b/i.test(t) && !/enduro trail/i.test(t)) c.enduro = 1;
    if (/\btrials?\b/i.test(t) && !/trail/i.test(t.replace(/trials?/ig, ''))) c.trials = 1;
    if (/\b(sand|paddle)\b/i.test(t)) c.sand = 1;
    if (/\b(scooter|moped|city grip|sc ?\d+|sr ?0\d\d|raven|kwick)\b/i.test(t)) c.scooter = 1;
    if (/\b(cruiser|w\/w|white ?wall|long haul|harley|commander|american elite|elite|cruisetec|veetwin|bagger|custom|classic|vintage|ironhorse)\b/i.test(t)) c.cruiser = 1;
    if (/\b(sport|sportmax|touring|tour master|street|road|roadtec|angel|diablo|rosso|sportec|racetec|battlax|pilot|power|qualifier|verge|stealth|hypersport|sport touring|gt|radial)\b/i.test(t) &&
        !/\b(dual sport|off-?road|adventure)\b/i.test(t) && !c.mx && !c.dual && !c.scooter) c.street = 1;

    // Shinko numbered off-road series (504, 505, 520, 524, 525, 540, 546, 216MX, R520DC, Cheater)
    if (/\b(?:r?5[0-4]\d\w*|216\w*)\s+(?:cheater\s+)?series\b|\bcheater\b/i.test(t)) c.mx = 1;

    if (/(atv|utv|sxs|quad|rip ?saw)/i.test(t) || (size && size.kind === 'flot' && +size.r <= 15)) c = { atv: 1 };

    var ids = Object.keys(c);
    if (!ids.length && size) {
      // No tag or keyword: fall back to what the size says
      if (size.a === '100') ids = ['mx'];
      else if (+size.r <= 14) ids = ['scooter'];
      else if (size.kind === 'inch' && /\bTT\b|tube type/i.test(title)) ids = ['dual'];
      else ids = ['street'];
    }
    return ids;
  }

  function terrainOf(title) {
    var t = String(title);
    var m;
    if ((m = /\b(mid[\s-]?soft|medium[\s-]?soft|soft[\s-]?(?:to[\s-]?)?intermediate|soft)\b/i.exec(t))) return { id: /intermediate/i.test(m[1]) ? 'soft' : 'soft', label: tidy(m[1]) };
    if ((m = /\b(mid[\s-]?hard|medium[\s-]?hard|intermediate[\s-]?(?:to[\s-]?)?hard|hard)\b/i.exec(t)) && !/hard enduro/i.test(t)) return { id: 'hard', label: tidy(m[1]) };
    if ((m = /\b(intermediate|medium|mid)\b/i.exec(t))) return { id: 'intermediate', label: tidy(m[1]) };
    if ((m = /\b(sand|mud|paddle)\b/i.exec(t))) return { id: 'sandmud', label: tidy(m[1]) };
    return null;
  }
  function tidy(s) {
    return String(s).toLowerCase().replace(/[\s-]+/g, ' ').replace(/^\w/, function (x) { return x.toUpperCase(); });
  }

  function positionOf(title, tags) {
    var t = String(title);
    if (/\bfront\s*(\/|&|and|or)\s*rear\b|\brear\s*(\/|&|and|or)\s*front\b|\bf\/r\b/i.test(t)) return 'both';
    var f = /\bfront\b/i.test(t), r = /\brear\b/i.test(t);
    if (f && r) return 'both';
    if (f) return 'front';
    if (r) return 'rear';
    var tg = (tags || []).join('|');
    var tf = /Tire [^|]*Front/i.test(tg), tr = /Tire [^|]*Rear/i.test(tg);
    if (tf && tr) return 'both';
    if (tf) return 'front';
    if (tr) return 'rear';
    return '';
  }

  // Product name without brand, the word "Tire", size, position, load index and part number.
  function modelName(title, vendor, size) {
    var t = String(title);
    if (vendor) {
      var v = vendor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      t = t.replace(new RegExp('^\\s*' + v + '\\s*', 'i'), '');
    }
    t = t.replace(/^(?:[A-Z&\- ]{3,}\s)?(?=tire\b)/, ''); // upper-case brand that differs from vendor (e.g. "MAXXIS")
    t = t.replace(/^\s*tires?\b\s*[-:]?\s*/i, '');
    var cut = t.length;
    if (size) {
      var s2 = findSize(t);
      if (s2) cut = Math.min(cut, s2.index);
    }
    var pm = /(\s-\s|\s)(front\s*\/\s*rear|front|rear)\b/i.exec(t);
    if (pm) cut = Math.min(cut, pm.index);
    var name = t.slice(0, cut).replace(/[\s\-–|,]+$/g, '').replace(/^[\s\-–|]+/, '').trim();
    if (!name) name = t.replace(/\s+-\s+.*$/, '').trim() || String(title);
    return name;
  }

  function isTireTitle(title, size) {
    var t = String(title).replace(/tube ?type|tubeless/ig, '');
    if (!/\btires?\b/i.test(t)) return false;
    if (size) return !ACCESSORY_STRONG.test(t);
    return !ACCESSORY.test(t);
  }

  // Raw index product -> tire record (or null for non-tires).
  // raw = { h, t, v, u, p, c, a, img, vid, tags }
  function parseProduct(raw) {
    var title = raw.t || '';
    var size = findSize(title);
    if (!isTireTitle(title, size)) return null;
    var tags = raw.tags || [];
    var load = null;
    if (size) {
      // Load / speed index right after the size: "62M", "(75w)", "- Rear - 70W"
      var after = title.slice(size.index + size.len);
      var lm = /(?:^|[\s(])(\d{2,3})([A-Z])\)?(?=\s|$)/i.exec(after);
      if (lm) load = lm[1] + lm[2].toUpperCase();
    }
    var cons = '';
    if (size && /R/.test(size.cons)) cons = 'Radial';
    else if (/\bradial\b/i.test(title)) cons = 'Radial';
    else if (/\bbelted\b/i.test(title)) cons = 'Belted bias';
    else if ((size && size.cons === 'B') || /\bbias\b/i.test(title)) cons = 'Bias';
    var tube = /\bTL\b|tubeless/i.test(title) ? 'Tubeless' : (/\bTT\b|tube type/i.test(title) ? 'Tube type' : '');
    var terr = terrainOf(title);
    return {
      h: raw.h, title: title, brand: normBrand(raw.v, title), url: raw.u, price: +raw.p || 0,
      compare: +raw.c || 0, avail: !!raw.a, img: raw.img || '', vid: raw.vid,
      size: size ? size.key : '', alias: size && size.alias ? size.alias : '', w: size ? size.w : '', a: size ? size.a : '', r: size ? size.r : '',
      kind: size ? size.kind : '', pos: positionOf(title, tags), cats: categorize(title, tags, size),
      terrain: terr ? terr.id : '', terrainLabel: terr ? terr.label : '', cons: cons, tube: tube, load: load,
      name: modelName(title, raw.v, size)
    };
  }

  function normBrand(v, title) {
    v = String(v || '').trim();
    if (!v) return '';
    if (v === v.toUpperCase() && v.length > 3) v = v.toLowerCase().replace(/\b\w/g, function (x) { return x.toUpperCase(); });
    return v;
  }

  function buildIndex(raws) {
    var out = [];
    for (var i = 0; i < raws.length; i++) {
      var r = parseProduct(raws[i]);
      if (r) out.push(r);
    }
    return out;
  }

  function topSizes(items, pos, n) {
    var cnt = {};
    items.forEach(function (it) {
      if (!it.size || !it.r) return;
      if (pos && it.pos !== pos) return;
      cnt[it.size] = (cnt[it.size] || 0) + 1;
    });
    return Object.keys(cnt).sort(function (a, b) { return cnt[b] - cnt[a]; }).slice(0, n || 6)
      .map(function (k) { return { size: k, count: cnt[k] }; });
  }

  var api = {
    findSize: findSize, parseQuery: parseQuery, parseProduct: parseProduct, buildIndex: buildIndex,
    topSizes: topSizes, sizeKey: sizeKey, positionOf: positionOf, categorize: categorize,
    terrainOf: terrainOf, modelName: modelName, CATEGORIES: CATEGORIES, TERRAINS: TERRAINS,
    boot: function () {}
  };

  /* ---- UI is added below (browser only) ---- */
  api.boot = function () {
    if (typeof document === 'undefined') return;
    var roots = document.querySelectorAll('[data-sm-tire-finder]');
    for (var i = 0; i < roots.length; i++) {
      if (!roots[i].__smTire) { roots[i].__smTire = true; ui(roots[i]); }
    }
  };

  /* ------------------------------------------------------------------ */
  /* Browser UI                                                          */
  /* ------------------------------------------------------------------ */

  var CACHE_MS = 30 * 60 * 1000;
  var CACHE_VER = 'smTireIdx:v2:';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function q(el, sel) { return el.querySelector(sel); }
  function fmtNum(n) { try { return Number(n).toLocaleString(); } catch (e) { return String(n); } }
  function cacheGet(key) {
    try {
      var v = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      if (v && v.ts && Date.now() - v.ts < CACHE_MS && v.raws && v.raws.length) return v;
    } catch (e) {}
    return null;
  }
  function cacheSet(key, raws, total) {
    try { window.sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), raws: raws, total: total })); } catch (e) {}
  }
  // The index template is served as text/html: read text, keep the JSON object only.
  function parseIndexText(txt) {
    var a = txt.indexOf('{'), b = txt.lastIndexOf('}');
    if (a < 0 || b < a) throw new Error('Bad index');
    return JSON.parse(txt.slice(a, b + 1));
  }

  function ui(rootEl) {
    var D = rootEl.dataset;
    // Tire types the shop doesn't sell (section setting, e.g. "atv,street"): left out of the filters, and a
    // tire is left out of the results when all of its types are hidden (a dual sport + street tire stays)
    var HIDE = (D.hideCats || '').split(',').map(function (x) { return x.trim(); }).filter(Boolean);
    function keepItems(list) {
      if (!HIDE.length) return list;
      return list.filter(function (it) {
        var shown = it.cats.filter(function (c) { return HIDE.indexOf(c) < 0; });
        if (!shown.length) return false;
        it.cats = shown;
        return true;
      });
    }
    var handle = D.handle || 'tires-2';
    var collUrl = D.collectionUrl || ('/collections/' + handle);
    var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var phone = D.phone || '(541) 803-8385';
    var phoneHref = 'tel:' + phone.replace(/[^\d+]/g, '');
    var PAGE = +D.pageSize || 48;
    var money;
    try {
      var nf = new Intl.NumberFormat(D.locale || undefined, { style: 'currency', currency: D.currency || 'USD' });
      money = function (c) { return nf.format(c / 100); };
    } catch (e) {
      money = function (c) { return '$' + (c / 100).toFixed(2); };
    }

    var el = {
      form: q(rootEl, '[data-tf-form]'), qi: q(rootEl, '[data-tf-q]'), qHint: q(rootEl, '[data-tf-q-hint]'),
      w: q(rootEl, '[data-tf-w]'), a: q(rootEl, '[data-tf-a]'), r: q(rootEl, '[data-tf-r]'),
      pos: q(rootEl, '[data-tf-pos]'), quick: q(rootEl, '[data-tf-quick]'),
      cats: q(rootEl, '[data-tf-cats]'), terrain: q(rootEl, '[data-tf-terrain]'), terrainWrap: q(rootEl, '[data-tf-terrain-wrap]'),
      brands: q(rootEl, '[data-tf-brands]'), stock: q(rootEl, '[data-tf-stock]'), sort: q(rootEl, '[data-tf-sort]'),
      count: q(rootEl, '[data-tf-count]'), status: q(rootEl, '[data-tf-status]'), grid: q(rootEl, '[data-tf-grid]'),
      more: q(rootEl, '[data-tf-more]'), empty: q(rootEl, '[data-tf-empty]'), toggle: q(rootEl, '[data-tf-toggle]'),
      panel: q(rootEl, '[data-tf-panel]'), clear: q(rootEl, '[data-tf-clear]'), active: q(rootEl, '[data-tf-active-count]'),
      indexed: q(rootEl, '[data-tf-indexed]')
    };

    var items = [];
    var brandCounts = {};
    var shown = PAGE;
    var lastList = [];
    var st = readUrl();

    /* ---------- URL state ---------- */
    function readUrl() {
      var p = new URLSearchParams(window.location.search);
      var s = parseQuery(p.get('size') || '');
      var list = function (k) { return (p.get(k) || '').split(',').map(function (x) { return x.trim(); }).filter(Boolean); };
      var pos = (p.get('pos') || '').toLowerCase();
      return {
        w: s.w, a: s.a, r: s.r,
        pos: pos === 'front' || pos === 'rear' ? pos : '',
        cats: list('cat'), terrain: list('terrain'), brands: list('brand'),
        stock: p.get('stock') === '1', sort: p.get('sort') || 'best'
      };
    }
    function writeUrl() {
      if (!window.history || !window.history.replaceState) return;
      var p = new URLSearchParams(window.location.search);
      ['size', 'pos', 'cat', 'terrain', 'brand', 'stock', 'sort'].forEach(function (k) { p.delete(k); });
      var key = sizeKey(st.w, st.a, st.r);
      if (key) p.set('size', key);
      if (st.pos) p.set('pos', st.pos);
      if (st.cats.length) p.set('cat', st.cats.join(','));
      if (st.terrain.length) p.set('terrain', st.terrain.join(','));
      if (st.brands.length) p.set('brand', st.brands.join(','));
      if (st.stock) p.set('stock', '1');
      if (st.sort && st.sort !== 'best') p.set('sort', st.sort);
      var qs = p.toString().replace(/%2F/g, '/').replace(/%2C/g, ',');
      window.history.replaceState(window.history.state, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
    }

    /* ---------- Filtering ---------- */
    function matches(it, skip) {
      if (skip !== 'size') {
        if (st.w && it.w !== st.w) return false;
        if (st.a && it.a !== st.a) return false;
        if (st.r && it.r !== st.r) return false;
      }
      if (st.pos && skip !== 'pos' && it.pos && it.pos !== 'both' && it.pos !== st.pos) return false;
      if (st.cats.length && skip !== 'cat') {
        var ok = false;
        for (var i = 0; i < it.cats.length; i++) if (st.cats.indexOf(it.cats[i]) > -1) { ok = true; break; }
        if (!ok) return false;
      }
      if (st.terrain.length && skip !== 'terrain' && st.terrain.indexOf(it.terrain) < 0) return false;
      if (st.brands.length && skip !== 'brand' && st.brands.indexOf(it.brand) < 0) return false;
      if (st.stock && skip !== 'stock' && !it.avail) return false;
      return true;
    }
    function sizeOnly(it, w, a) {
      if (w !== undefined && w && it.w !== w) return false;
      if (a !== undefined && a && it.a !== a) return false;
      return true;
    }
    function filtered() {
      var out = items.filter(function (it) { return matches(it); });
      var s = st.sort;
      out.sort(function (x, y) {
        if (s === 'price-asc') return x.price - y.price || cmpName(x, y);
        if (s === 'price-desc') return y.price - x.price || cmpName(x, y);
        if (s === 'brand') return cmpName(x, y);
        return score(x) - score(y) || (brandCounts[y.brand] || 0) - (brandCounts[x.brand] || 0) || x.price - y.price;
      });
      return out;
    }
    function cmpName(x, y) {
      return x.brand.localeCompare(y.brand) || x.name.localeCompare(y.name) || x.price - y.price;
    }
    function score(it) {
      var s = 0;
      if (!it.avail) s += 8;
      if (!it.size) s += 4;
      if (st.pos && it.pos !== st.pos) s += it.pos === 'both' ? 1 : 2;
      if (!it.img) s += 1;
      return s;
    }

    /* ---------- Controls ---------- */
    function countBy(list, fn) {
      var o = {};
      list.forEach(function (it) { var k = fn(it); if (k) o[k] = (o[k] || 0) + 1; });
      return o;
    }
    function widthSort(a, b) {
      // metric, then inch (3.00), then ATV (25x8)
      var ai = a.indexOf('x') > -1 ? 2 : a.indexOf('.') > -1 ? 1 : 0, bi = b.indexOf('x') > -1 ? 2 : b.indexOf('.') > -1 ? 1 : 0;
      if (ai !== bi) return ai - bi;
      return parseFloat(a) - parseFloat(b);
    }
    function fillSelect(sel, placeholder, counts, current, sorter, labelFn) {
      var keys = Object.keys(counts).sort(sorter);
      if (current && keys.indexOf(current) < 0) keys.unshift(current);
      var h = '<option value="">' + esc(placeholder) + '</option>';
      keys.forEach(function (k) {
        h += '<option value="' + esc(k) + '"' + (k === current ? ' selected' : '') + '>' +
          esc(labelFn ? labelFn(k) : k) + ' (' + (counts[k] || 0) + ')</option>';
      });
      sel.innerHTML = h;
      sel.value = current || '';
    }
    function renderSelects() {
      var base = items.filter(function (it) { return it.size && matches(it, 'size'); });
      fillSelect(el.w, 'Any width', countBy(base, function (it) { return it.w; }), st.w, widthSort);
      var inch = st.w && st.w.indexOf('.') > -1;
      if (inch) {
        el.a.innerHTML = '<option value="">Not used for inch sizes</option>';
        el.a.disabled = true;
      } else {
        el.a.disabled = false;
        var ba = base.filter(function (it) { return it.kind === 'metric' && sizeOnly(it, st.w); });
        fillSelect(el.a, 'Any aspect', countBy(ba, function (it) { return it.a; }), st.a, function (x, y) { return x - y; });
      }
      var br = base.filter(function (it) { return sizeOnly(it, st.w, st.a); });
      fillSelect(el.r, 'Any rim', countBy(br, function (it) { return it.r; }), st.r, function (x, y) { return x - y; },
        function (k) { return k + '"'; });
    }
    function pressed(btn, on) { btn.setAttribute('aria-pressed', on ? 'true' : 'false'); }
    function renderPos() {
      Array.prototype.forEach.call(el.pos.querySelectorAll('button[data-pos]'), function (b) {
        pressed(b, (b.getAttribute('data-pos') || '') === st.pos);
      });
    }
    function chip(attrs, label, count, on, disabled) {
      return '<button type="button" class="sm-tf-chip"' + attrs + ' aria-pressed="' + (on ? 'true' : 'false') + '"' +
        (disabled ? ' disabled' : '') + '>' + esc(label) +
        (count != null ? ' <span class="sm-tf-chip__n">' + count + '</span>' : '') + '</button>';
    }
    function renderQuick() {
      var key = sizeKey(st.w, st.a, st.r);
      var groups = st.pos ? [st.pos] : ['front', 'rear'];
      var h = '';
      groups.forEach(function (g) {
        var top = topSizes(items, g, st.pos ? 8 : 5);
        if (!top.length) return;
        h += '<div class="sm-tf-quick__row"><span class="sm-tf-quick__label" id="sm-tf-q-' + g + '">' + (g === 'front' ? 'Front' : 'Rear') + '</span>' +
          '<div class="sm-tf-quick__chips" role="group" aria-labelledby="sm-tf-q-' + g + '">';
        top.forEach(function (t) {
          h += chip(' data-quick="' + esc(t.size) + '" data-quick-pos="' + g + '"', t.size, null, key === t.size && st.pos === g);
        });
        h += '</div></div>';
      });
      el.quick.innerHTML = h;
    }
    function renderFacets() {
      // Category
      var cc = {};
      items.forEach(function (it) { if (matches(it, 'cat')) it.cats.forEach(function (c) { cc[c] = (cc[c] || 0) + 1; }); });
      el.cats.innerHTML = CATEGORIES.filter(function (c) { return HIDE.indexOf(c.id) < 0; }).map(function (c) {
        var on = st.cats.indexOf(c.id) > -1;
        return chip(' data-cat="' + c.id + '"', c.label, cc[c.id] || 0, on, !on && !cc[c.id]);
      }).join('');
      // Compound / terrain: only the ones present in the current results
      var tc = countBy(items.filter(function (it) { return matches(it, 'terrain'); }), function (it) { return it.terrain; });
      var anyT = TERRAINS.some(function (t) { return tc[t.id] || st.terrain.indexOf(t.id) > -1; });
      if (el.terrainWrap) el.terrainWrap.hidden = !anyT;
      el.terrain.innerHTML = TERRAINS.filter(function (t) { return tc[t.id] || st.terrain.indexOf(t.id) > -1; }).map(function (t) {
        return chip(' data-terrain="' + t.id + '"', t.label, tc[t.id] || 0, st.terrain.indexOf(t.id) > -1);
      }).join('');
      // Brand
      var bc = countBy(items.filter(function (it) { return matches(it, 'brand'); }), function (it) { return it.brand; });
      var names = Object.keys(brandCounts).filter(function (b) { return bc[b] || st.brands.indexOf(b) > -1; })
        .sort(function (x, y) { return (bc[y] || 0) - (bc[x] || 0) || x.localeCompare(y); });
      el.brands.innerHTML = names.length ? names.map(function (b, i) {
        var id = 'sm-tf-b-' + i;
        return '<li><input type="checkbox" id="' + id + '" value="' + esc(b) + '"' + (st.brands.indexOf(b) > -1 ? ' checked' : '') +
          '><label for="' + id + '">' + esc(b) + ' <span class="sm-tf-chip__n">' + (bc[b] || 0) + '</span></label></li>';
      }).join('') : '<li class="sm-tf-muted">No brands for this search</li>';
      el.stock.checked = st.stock;
      el.sort.value = st.sort;
      var n = st.cats.length + st.terrain.length + st.brands.length + (st.stock ? 1 : 0);
      if (el.active) el.active.textContent = n ? '(' + n + ')' : '';
      if (el.clear) el.clear.hidden = !n;
    }

    /* ---------- Results ---------- */
    var POS_LABEL = { front: 'Front', rear: 'Rear', both: 'Front/Rear' };
    function card(it) {
      var badges = '';
      if (it.size) badges += '<li class="sm-tf-badge sm-tf-badge--size">' + esc(it.alias ? it.alias + ' (' + it.size + ')' : it.size) + (it.cons === 'Radial' ? ' R' : '') + '</li>';
      if (it.pos) badges += '<li class="sm-tf-badge">' + POS_LABEL[it.pos] + '</li>';
      if (it.terrainLabel) badges += '<li class="sm-tf-badge sm-tf-badge--terrain">' + esc(it.terrainLabel) + '</li>';
      var price = it.price ? '<span class="sm-tf-card__price' + (it.compare > it.price ? ' is-sale' : '') + '">' +
        (it.compare > it.price ? '<span class="sm-tf-vh">Sale price </span>' : '') + money(it.price) + '</span>' +
        (it.compare > it.price ? ' <s class="sm-tf-card__was"><span class="sm-tf-vh">Regular price </span>' + money(it.compare) + '</s>' : '') : '';
      var label = esc(it.name + (it.size ? ' ' + it.size : ''));
      var meta = [it.load, it.cons, it.tube].filter(Boolean).join(' · ');
      return '<li class="sm-tf-card">' +
        '<a class="sm-tf-card__media" href="' + esc(it.url) + '" tabindex="-1" aria-hidden="true">' +
        (it.img ? '<img src="' + esc(it.img) + '" alt="" width="240" height="240" loading="lazy" decoding="async">' : '<span class="sm-tf-card__noimg"></span>') +
        '</a><div class="sm-tf-card__body">' +
        '<p class="sm-tf-card__brand">' + esc(it.brand) + '</p>' +
        '<h3 class="sm-tf-card__name"><a href="' + esc(it.url) + '" title="' + esc(it.title) + '">' + esc(it.name) + '</a></h3>' +
        (badges ? '<ul class="sm-tf-card__badges" aria-label="Tire details">' + badges + '</ul>' : '') +
        (meta ? '<p class="sm-tf-card__meta">' + esc(meta) + '</p>' : '') +
        '<p class="sm-tf-card__prices">' + price + '</p>' +
        '<div class="sm-tf-card__actions">' +
        (it.avail && it.vid
          ? '<button type="button" class="sm-tf-btn sm-tf-btn--add" data-add="' + esc(it.vid) + '" aria-label="Add ' + label + ' to cart">Add to cart</button>'
          : '<button type="button" class="sm-tf-btn sm-tf-btn--add" disabled>Out of stock</button>') +
        '<a class="sm-tf-btn sm-tf-btn--ghost" href="' + esc(it.url) + '" aria-label="View ' + label + '">View</a>' +
        '</div><p class="sm-tf-card__msg" role="status"></p></div></li>';
    }
    function describe(n) {
      var key = sizeKey(st.w, st.a, st.r);
      var noun = n === 1 ? 'tire' : 'tires';
      var posTxt = st.pos ? ' ' + st.pos : '';
      if (key) return fmtNum(n) + ' ' + noun + ' fit ' + key + posTxt;
      return fmtNum(n) + (st.pos ? ' ' + st.pos : '') + ' ' + noun;
    }
    var countTimer;
    function renderResults(keepShown) {
      lastList = filtered();
      if (!keepShown) shown = PAGE;
      var list = lastList.slice(0, shown);
      el.grid.innerHTML = list.map(card).join('');
      el.grid.setAttribute('aria-busy', 'false');
      var rest = lastList.length - list.length;
      el.more.hidden = rest <= 0;
      var mb = q(el.more, 'button');
      if (mb) mb.textContent = 'Show ' + Math.min(PAGE, rest) + ' more (' + fmtNum(rest) + ' left)';
      // Polite live update, debounced so typing doesn't flood screen readers
      clearTimeout(countTimer);
      var txt = describe(lastList.length);
      countTimer = setTimeout(function () { el.count.textContent = txt; }, 250);
      renderEmpty();
    }
    function renderEmpty() {
      // Still loading more collections: don't say "no tires" yet.
      if (lastList.length || loadingMore) { el.empty.hidden = true; el.empty.innerHTML = ''; return; }
      var key = sizeKey(st.w, st.a, st.r);
      var h = '<h3 class="sm-tf-empty__title">No tires match' + (key ? ' ' + esc(key) : '') + (st.pos ? ' ' + st.pos : '') + '</h3>';
      if (st.r && st.w) {
        // Nearest sizes: same rim, closest widths (one width step first)
        var inch = st.w.indexOf('.') > -1;
        var near = {};
        items.forEach(function (it) {
          if (!it.size || it.r !== st.r || (it.w.indexOf('.') > -1) !== inch || (it.kind === 'flot') !== (st.w.indexOf('x') > -1) || it.size === key) return;
          if (st.pos && it.pos && it.pos !== 'both' && it.pos !== st.pos) return;
          near[it.size] = near[it.size] || { size: it.size, n: 0, d: Math.abs(parseFloat(it.w) - parseFloat(st.w)) * (inch ? 40 : 1) + (st.a && it.a ? Math.abs(+it.a - +st.a) / 10 : 0) };
          near[it.size].n++;
        });
        var sug = Object.keys(near).map(function (k) { return near[k]; }).sort(function (x, y) { return x.d - y.d || y.n - x.n; }).slice(0, 4);
        if (sug.length) {
          h += '<p>Closest sizes we stock on a ' + esc(st.r) + '" rim (check your owner’s manual before changing size):</p><div class="sm-tf-empty__chips">' +
            sug.map(function (s) { return chip(' data-quick="' + esc(s.size) + '"', s.size, s.n, false); }).join('') + '</div>';
        }
      }
      if (st.pos && key) {
        var anyPos = items.filter(function (it) { return it.size === key; }).length;
        if (anyPos) h += '<p><button type="button" class="sm-tf-link" data-tf-anypos>See ' + anyPos + ' ' + esc(key) + (anyPos === 1 ? ' tire' : ' tires') + ' for any position</button></p>';
      }
      var nf = st.cats.length + st.terrain.length + st.brands.length + (st.stock ? 1 : 0);
      if (nf) h += '<p><button type="button" class="sm-tf-link" data-tf-clear-inline>Clear filters</button> to see every brand and type.</p>';
      h += '<p class="sm-tf-empty__call"><a class="sm-tf-btn sm-tf-btn--gold" href="' + esc(phoneHref) + '">Call ' + esc(phone) + '</a> We can order any size.</p>';
      el.empty.innerHTML = h;
      el.empty.hidden = false;
    }
    function renderAll(keepShown) {
      renderSelects();
      renderPos();
      renderQuick();
      renderFacets();
      renderResults(keepShown);
      writeUrl();
    }

    /* ---------- Events ---------- */
    function setSize(s, fromText) {
      st.w = s.w || ''; st.a = s.a || ''; st.r = s.r || '';
      if (!fromText) el.qi.value = sizeKey(st.w, st.a, st.r);
      renderAll();
    }
    var typeTimer;
    function fromText(final) {
      var v = el.qi.value.trim();
      if (!v) { if (el.qHint) el.qHint.textContent = ''; setSize({}, true); return; }
      var s = parseQuery(v);
      if (!s.w) {
        if (final && el.qHint) el.qHint.textContent = 'Try a size like 110/90-19, 80/100-21 or 3.00-21.';
        return;
      }
      if (el.qHint) el.qHint.textContent = '';
      setSize(s, true);
    }
    el.qi.addEventListener('input', function () { clearTimeout(typeTimer); typeTimer = setTimeout(function () { fromText(false); }, 350); });
    el.form.addEventListener('submit', function (e) {
      e.preventDefault();
      clearTimeout(typeTimer);
      fromText(true);
      var key = sizeKey(st.w, st.a, st.r);
      if (key) el.qi.value = key;
      var res = q(rootEl, '[data-tf-results]');
      if (res && res.getBoundingClientRect().top > window.innerHeight * 0.8) res.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    el.w.addEventListener('change', function () { setSize({ w: el.w.value, a: '', r: '' }); });
    el.a.addEventListener('change', function () { setSize({ w: st.w, a: el.a.value, r: '' }); });
    el.r.addEventListener('change', function () { setSize({ w: st.w, a: st.a, r: el.r.value }); });
    el.pos.addEventListener('click', function (e) {
      var b = e.target.closest('button[data-pos]');
      if (!b) return;
      st.pos = b.getAttribute('data-pos') || '';
      renderAll();
    });
    rootEl.addEventListener('click', function (e) {
      var b = e.target.closest('[data-quick]');
      if (b) {
        var p = b.getAttribute('data-quick-pos');
        if (p) st.pos = p;
        setSize(parseQuery(b.getAttribute('data-quick')));
        var again = q(rootEl, '[data-quick="' + b.getAttribute('data-quick') + '"]');
        if (again && el.quick.contains(again)) again.focus();
        else el.count.focus();
        return;
      }
      if (e.target.closest('[data-tf-anypos]')) {
        st.pos = '';
        renderAll();
        el.count.focus();
        return;
      }
      if (e.target.closest('[data-tf-clear-inline]') || e.target.closest('[data-tf-clear]')) {
        st.cats = []; st.terrain = []; st.brands = []; st.stock = false;
        renderAll();
        if (el.clear && !el.clear.hidden) el.clear.focus(); else el.count.focus();
      }
    });
    function toggleIn(arr, v) { var i = arr.indexOf(v); if (i > -1) arr.splice(i, 1); else arr.push(v); }
    function refocus(sel) { var n = q(rootEl, sel); if (n) n.focus(); }
    el.cats.addEventListener('click', function (e) {
      var b = e.target.closest('[data-cat]'); if (!b) return;
      var id = b.getAttribute('data-cat');
      toggleIn(st.cats, id); renderAll(); refocus('[data-cat="' + id + '"]');
    });
    el.terrain.addEventListener('click', function (e) {
      var b = e.target.closest('[data-terrain]'); if (!b) return;
      var id = b.getAttribute('data-terrain');
      toggleIn(st.terrain, id); renderAll(); refocus('[data-terrain="' + id + '"]');
    });
    el.brands.addEventListener('change', function (e) {
      var c = e.target; if (!c || c.type !== 'checkbox') return;
      toggleIn(st.brands, c.value); renderAll();
      var again = Array.prototype.filter.call(el.brands.querySelectorAll('input'), function (i) { return i.value === c.value; })[0];
      if (again) again.focus();
    });
    el.stock.addEventListener('change', function () { st.stock = el.stock.checked; renderAll(); });
    el.sort.addEventListener('change', function () { st.sort = el.sort.value; renderAll(); });
    q(el.more, 'button').addEventListener('click', function () {
      var from = shown;
      shown += PAGE;
      renderResults(true);
      var links = el.grid.querySelectorAll('.sm-tf-card__name a');
      if (links[from]) links[from].focus();
    });
    if (el.toggle && el.panel) {
      el.toggle.addEventListener('click', function () {
        var open = el.toggle.getAttribute('aria-expanded') !== 'true';
        el.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        el.panel.classList.toggle('is-open', open);
      });
    }
    // Add to cart (snippets/sm-cart-drawer.liquid opens the drawer for any /cart/add)
    el.grid.addEventListener('click', function (e) {
      var b = e.target.closest('[data-add]');
      if (!b || b.disabled) return;
      var msg = b.closest('.sm-tf-card').querySelector('.sm-tf-card__msg');
      var label = b.textContent;
      b.disabled = true;
      b.setAttribute('aria-busy', 'true');
      b.textContent = 'Adding…';
      msg.textContent = '';
      fetch(rootPath + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: Number(b.getAttribute('data-add')), quantity: 1 })
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (j) {
          if (!r.ok) throw new Error((j && (j.description || j.message)) || 'Could not add this tire. Please try again.');
          b.textContent = 'Added';
          msg.textContent = 'Added to your cart.';
          setTimeout(function () { b.textContent = label; b.disabled = false; b.removeAttribute('aria-busy'); }, 1800);
        });
      }).catch(function (err) {
        b.textContent = label; b.disabled = false; b.removeAttribute('aria-busy');
        msg.textContent = err && err.message ? err.message : 'Could not add this tire. Please try again.';
      });
    });

    /* ---------- Loading the index ---------- */
    function status(html) { el.status.innerHTML = html; el.status.hidden = !html; }
    function progress(done, total) {
      var pct = total ? Math.round(done / total * 100) : 0;
      status('<label class="sm-tf-progress"><span>Loading tire catalog… ' + fmtNum(done) + (total ? ' of ' + fmtNum(total) : '') + ' products</span>' +
        '<progress max="100" value="' + pct + '">' + pct + '%</progress></label>');
    }
    function fetchPage(src, n) {
      var url = src + (src.indexOf('?') > -1 ? '&' : '?') + 'view=tire-index&page=' + n;
      return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json, text/plain, */*' } })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .then(parseIndexText);
    }
    function ready(raws, total) {
      items = keepItems(buildIndex(raws));
      brandCounts = countBy(items, function (it) { return it.brand; });
      status('');
      if (el.indexed) el.indexed.textContent = fmtNum(items.length) + ' tires indexed from ' + fmtNum(total || raws.length) + ' products' + (sources.length > 1 ? ' in ' + sources.length + ' collections' : '');
      el.qi.value = sizeKey(st.w, st.a, st.r);
      renderAll();
    }
    // Source collections: data-sources="tires-1,tires-2,..." (section setting), else this page's collection.
    var sources = (D.sources || '').split(',').map(function (x) { return x.trim(); }).filter(Boolean)
      .map(function (h) { return h.charAt(0) === '/' ? h : rootPath + 'collections/' + h; });
    if (!sources.length) sources = [collUrl];
    // Run tasks (functions returning promises) at most 6 at a time.
    function pool(tasks) {
      var i = 0;
      function next() { return i < tasks.length ? tasks[i++]().then(next) : Promise.resolve(); }
      var ws = [];
      for (var k = 0; k < Math.min(6, tasks.length); k++) ws.push(next());
      return Promise.all(ws);
    }
    var loadingMore = false;
    function load() {
      var key = CACHE_VER + sources.join(',');
      var cached = cacheGet(key);
      if (cached) return ready(cached.raws, cached.total);
      progress(0, 0);
      el.grid.setAttribute('aria-busy', 'true');
      var pages = {}, done = 0, total = 0, lastPartial = 0;
      loadingMore = true;
      // Same product in several collections: keep it once.
      function collected() {
        var seen = {}, raws = [];
        sources.forEach(function (src) {
          Object.keys(pages).forEach(function (k) {
            if (k.indexOf(src + '#') !== 0) return;
            pages[k].forEach(function (r) { if (!seen[r.h]) { seen[r.h] = 1; raws.push(r); } });
          });
        });
        return raws;
      }
      function got(src, n, d) {
        pages[src + '#' + n] = d.products || [];
        done += pages[src + '#' + n].length;
        // Show results while the rest loads (at most every 1.5 s); the progress bar stays.
        var now = Date.now();
        if (now - lastPartial > 1500) {
          lastPartial = now;
          items = keepItems(buildIndex(collected()));
          brandCounts = countBy(items, function (it) { return it.brand; });
          renderAll();
        }
        progress(done, total);
      }
      var rest = [];
      pool(sources.map(function (src) {
        return function () {
          return fetchPage(src, 1).then(function (first) {
            total += +first.total || 0;
            got(src, 1, first);
            for (var p = 2; p <= Math.max(1, +first.pages || 1); p++) rest.push([src, p]);
          });
        };
      })).then(function () {
        return pool(rest.map(function (sp) {
          return function () { return fetchPage(sp[0], sp[1]).then(function (d) { got(sp[0], sp[1], d); }); };
        }));
      }).then(function () {
        var raws = collected();
        loadingMore = false;
        cacheSet(key, raws, total);
        ready(raws, total);
      }).catch(function () {
        loadingMore = false;
        el.grid.innerHTML = '';
        el.grid.setAttribute('aria-busy', 'false');
        status('<p class="sm-tf-error">We couldn’t load the tire list. <button type="button" class="sm-tf-link" data-tf-retry>Try again</button> or call <a href="' +
          esc(phoneHref) + '">' + esc(phone) + '</a>.</p>');
        var rb = q(rootEl, '[data-tf-retry]');
        if (rb) rb.addEventListener('click', load);
      });
    }

    el.qi.value = sizeKey(st.w, st.a, st.r);
    load();
  }

  return api;
});
